/**
 * Mobile point-of-care money visibility matrix (human-accepted 2026-07-22).
 *
 * Binding pack:
 * `reports/relay-inbox/care-delivery-role-visibility-matrix-2026-07-22/`
 *
 * Rules (summary):
 * - CARE_WORKER / PCW / EN / RN / Allied / Agency / ClinicalLead: budget SIGNAL only
 *   (no quarter $); contribution CAT only; no billable/subsidy/unit price.
 * - Agency: never quarter $ (even if employed CW ever gains remaining-$).
 * - TEAM_LEADER / COORDINATOR / CASE_MANAGER: current-quarter remaining $ only
 *   on mobile; no lifetime, no billable/subsidy/unit price on visit.
 * - Lifetime contribution/cap: web only (never mobile DTO).
 * - UI-only hide is insufficient — strip fields in API/DTO for the cohort.
 */

export type MobileMoneyBudgetMode = 'signal' | 'remaining_only' | 'hide';

export type MobileMoneyContributionMode = 'category_only' | 'category_percent' | 'hide';

export type BudgetPressureSignal = 'ok' | 'low' | 'unknown';

export interface MobileMoneyVisibility {
  /** Current-quarter budget dollars on mobile. */
  budgetMode: MobileMoneyBudgetMode;
  /** Whether visit payload may include billable / client contribution / subsidy. */
  showServiceFinancials: boolean;
  /** Whether unit price / rate card fields may appear. */
  showUnitPrice: boolean;
  /** Contribution label mode on plan spine / inclusions. */
  contributionMode: MobileMoneyContributionMode;
  /**
   * Lifetime contribution totals + caps on mobile.
   * Locked false for all mobile roles (web-only).
   */
  showLifetimeContributions: boolean;
}

const ELEVATED_REMAINING: ReadonlySet<string> = new Set([
  'TEAM_LEADER',
  'COORDINATOR',
  'CASE_MANAGER',
]);

const FRONTLINE_SIGNAL: ReadonlySet<string> = new Set([
  'CARE_WORKER',
  'PERSONAL_CARE_WORKER',
  'ENROLLED_NURSE',
  'REGISTERED_NURSE',
  'ALLIED_HEALTH',
  'AGENCY_CONTRACTOR',
  'CLINICAL_LEAD',
  'LIFESTYLE_OFFICER',
]);

/** Roles that may see category + contribution % on mobile (not absolute $). */
const CONTRIBUTION_PERCENT_ROLES: ReadonlySet<string> = new Set([
  'COORDINATOR',
  'CASE_MANAGER',
  'FACILITY_MANAGER',
  'TENANT_ADMIN',
  'SYSTEM_ADMIN',
  'FINANCE_OFFICER',
]);

export function normalizeMobileMoneyRole(role: string | null | undefined): string {
  if (!role) return 'CARE_WORKER';
  const upper = role.trim().toUpperCase();
  if (upper === 'PERSONAL_CARE_WORKER') return 'CARE_WORKER';
  return upper;
}

/**
 * Resolve mobile money visibility for a staff role.
 * Server/DTO strip must use this — never trust a client-supplied mode.
 */
export function resolveMobileMoneyVisibility(
  role: string | null | undefined,
): MobileMoneyVisibility {
  const r = normalizeMobileMoneyRole(role);

  // Finance-grade web roles if they ever open the worker app: remaining-only
  // quarter context, still no visit billable row / lifetime on mobile.
  if (
    r === 'FINANCE_OFFICER' ||
    r === 'TENANT_ADMIN' ||
    r === 'SYSTEM_ADMIN' ||
    r === 'FACILITY_MANAGER'
  ) {
    return {
      budgetMode: 'remaining_only',
      showServiceFinancials: false,
      showUnitPrice: false,
      contributionMode: CONTRIBUTION_PERCENT_ROLES.has(r) ? 'category_percent' : 'category_only',
      showLifetimeContributions: false,
    };
  }

  if (ELEVATED_REMAINING.has(r)) {
    return {
      budgetMode: 'remaining_only',
      showServiceFinancials: false,
      showUnitPrice: false,
      contributionMode: CONTRIBUTION_PERCENT_ROLES.has(r) ? 'category_percent' : 'category_only',
      showLifetimeContributions: false,
    };
  }

  if (FRONTLINE_SIGNAL.has(r) || r === 'CARE_WORKER') {
    return {
      budgetMode: 'signal',
      showServiceFinancials: false,
      showUnitPrice: false,
      contributionMode: 'category_only',
      showLifetimeContributions: false,
    };
  }

  // Unknown role → fail closed (strictest frontline posture).
  return {
    budgetMode: 'signal',
    showServiceFinancials: false,
    showUnitPrice: false,
    contributionMode: 'category_only',
    showLifetimeContributions: false,
  };
}

/**
 * Map usage of current-quarter budget to a non-monetary pressure signal.
 * Matches visit BudgetIndicator thresholds: >90% spent → low.
 */
export function budgetPressureFromAmounts(input: {
  allocated?: number | null;
  spent?: number | null;
  remaining?: number | null;
}): BudgetPressureSignal {
  const allocated = Number(input.allocated);
  const spent = Number(input.spent);
  if (!Number.isFinite(allocated) || allocated <= 0) {
    // Prefer remaining-only path when allocated unknown.
    const remaining = Number(input.remaining);
    if (!Number.isFinite(remaining)) return 'unknown';
    // Without allocated we cannot compute low vs ok honestly.
    return 'unknown';
  }
  if (!Number.isFinite(spent) || spent < 0) return 'unknown';
  const usagePercent = (spent / allocated) * 100;
  if (usagePercent > 90) return 'low';
  return 'ok';
}

/** JSON keys that must never appear on mobile visit DTOs for frontline cohorts. */
export const MOBILE_VISIT_FORBIDDEN_MONEY_KEYS = [
  'billableAmount',
  'billable_amount',
  'clientContribution',
  'client_contribution',
  'governmentSubsidy',
  'government_subsidy',
  'pricePerUnit',
  'price_per_unit',
  'unitCost',
  'unit_cost',
  'lifetimeContributions',
  'lifetime_contributions',
  'lifetimeCap',
  'lifetime_cap',
  'claimStatus',
  'claim_status',
  'financialsPostingState',
  'financials_posting_state',
] as const;

/**
 * Parent `services_delivered` columns safe for mobile visit detail when
 * `showServiceFinancials` is false. Keep in sync with live schema + care-delivery
 * migrations (20270210). Do not invent names — missing columns fail PostgREST.
 *
 * Explicitly **omits**: billable_amount, client_contribution, government_subsidy,
 * claim_status, financials_posting_state, cost_centre, primary_funding_account_id,
 * and other finance-only fields so money never rides the visit select wire for
 * frontline cohorts (phase-2 hardening after matrix D1–D6).
 */
export const MOBILE_VISIT_SERVICES_DELIVERED_COLUMNS = [
  'id',
  'tenant_id',
  'client_id',
  'care_plan_id',
  'care_worker_id',
  'service_type',
  'service_category',
  'scheduled_start',
  'scheduled_end',
  'actual_start',
  'actual_end',
  'status',
  'cancellation_reason',
  'notes',
  'tasks_completed',
  'funding_source',
  'gps_clock_in',
  'gps_clock_out',
  'travel_time_minutes',
  'travel_distance_km',
  'break_minutes',
  'goal_ids',
  'created_at',
  'updated_at',
  'client_confirmed',
  'client_confirmed_method',
  'client_confirmed_not_obtained_reason',
  'client_confirmed_not_obtained_detail',
  'service_summary',
  'service_area_id',
  'idempotency_key',
  'deemed_delivered',
  'deemed_delivered_reason',
  'roster_shift_id',
  'care_plan_service_inclusion_id',
  'delivery_evidence_group_id',
  'delivery_brief_snapshot',
  'time_source',
  'client_signature_sha256',
  'client_signature_body_sha256',
  'client_signature_signer_name',
  'client_signature_method',
  'client_signature_signed_at',
] as const;

/** Money / finance columns that must never appear in the frontline parent select. */
export const MOBILE_VISIT_SERVICES_DELIVERED_MONEY_COLUMNS = [
  'billable_amount',
  'client_contribution',
  'government_subsidy',
  'claim_status',
  'financials_posting_state',
  'cost_centre',
  'primary_funding_account_id',
] as const;

/**
 * Build PostgREST parent select for mobile visit detail.
 * When financials are hidden, uses the operational column allowlist (no `*`).
 */
export function buildMobileVisitParentSelect(visibility: MobileMoneyVisibility): string {
  if (visibility.showServiceFinancials) {
    return '*';
  }
  return MOBILE_VISIT_SERVICES_DELIVERED_COLUMNS.join(', ');
}

/**
 * Controlled RPC for mobile SAH visit-detail projection (migration 20270503).
 * Server builds role-aware JSON; client strip remains defence-in-depth.
 * Keep field allowlists aligned with MOBILE_VISIT_SERVICES_DELIVERED_COLUMNS
 * and the RPC body — never trust a client-supplied role for money mode.
 */
export const MOBILE_SAH_VISIT_DETAIL_PROJECTION_RPC =
  'get_mobile_sah_visit_detail_controlled' as const;

/** Nested claim-detail keys the projection may include (no unit price for frontline). */
export const MOBILE_VISIT_SAH_DETAIL_PROJECTION_KEYS = [
  'service_delivered_id',
  'item_description_code',
  'item_description',
  'unit_type',
  'quantity',
  'funding_source_code',
  'deemed_delivered',
] as const;

/** Client embed keys for visit execute (identity + contact strip only). */
export const MOBILE_VISIT_CLIENT_PROJECTION_KEYS = [
  'id',
  'first_name',
  'last_name',
  'preferred_name',
  'photo_url',
  'primary_address',
  'phone',
  'risk_level',
] as const;

export const MOBILE_BUDGET_FORBIDDEN_FOR_SIGNAL_KEYS = [
  'allocated',
  'spent',
  'remaining',
  'allocatedAmount',
  'spentAmount',
  'remainingAmount',
  'allocated_amount',
  'spent_amount',
  'remaining_amount',
] as const;

/**
 * Strip visit detail money fields according to visibility.
 * Mutates a shallow clone of the visit object graph for nested inclusion/detail.
 */
export function stripVisitMoneyFields<T extends Record<string, unknown>>(
  visit: T,
  visibility: MobileMoneyVisibility,
): T {
  const out = { ...visit } as Record<string, unknown>;

  if (!visibility.showServiceFinancials) {
    delete out.billableAmount;
    delete out.billable_amount;
    delete out.clientContribution;
    delete out.client_contribution;
    delete out.governmentSubsidy;
    delete out.government_subsidy;
    delete out.claimStatus;
    delete out.claim_status;
    delete out.financialsPostingState;
    delete out.financials_posting_state;
    delete out.costCentre;
    delete out.cost_centre;
    delete out.primaryFundingAccountId;
    delete out.primary_funding_account_id;
  }

  const stripNestedMoney = (raw: unknown): unknown => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
    const row = { ...(raw as Record<string, unknown>) };
    if (!visibility.showUnitPrice) {
      delete row.pricePerUnit;
      delete row.price_per_unit;
      delete row.unitCost;
      delete row.unit_cost;
    }
    if (visibility.contributionMode === 'category_only' || visibility.contributionMode === 'hide') {
      delete row.contributionPercentage;
      delete row.contribution_percentage;
    }
    return row;
  };

  if (out.sahDetail != null) out.sahDetail = stripNestedMoney(out.sahDetail);
  if (out.sah_detail != null) out.sah_detail = stripNestedMoney(out.sah_detail);
  if (out.carePlanServiceInclusion != null) {
    out.carePlanServiceInclusion = stripNestedMoney(out.carePlanServiceInclusion);
  }
  if (out.care_plan_service_inclusion != null) {
    out.care_plan_service_inclusion = stripNestedMoney(out.care_plan_service_inclusion);
  }

  // Quarter budget on visit
  const qKey = out.sahQuarterBudget != null ? 'sahQuarterBudget' : 'sah_quarter_budget';
  const q = out[qKey];
  if (q && typeof q === 'object' && !Array.isArray(q)) {
    if (visibility.budgetMode === 'signal' || visibility.budgetMode === 'hide') {
      out[qKey] = null;
    } else if (visibility.budgetMode === 'remaining_only') {
      const row = q as Record<string, unknown>;
      const remaining = row.remainingAmount ?? row.remaining_amount ?? null;
      const quarter = row.quarter ?? null;
      const id = row.id ?? null;
      out[qKey] = {
        id,
        quarter,
        remainingAmount: remaining,
        // Explicitly omit allocated/spent/carry for remaining-only cohort.
      };
    }
  }

  // Never ship lifetime on mobile visit payloads.
  delete out.lifetimeContributions;
  delete out.lifetimeCap;
  delete out.isApproachingCap;

  return out as T;
}

export interface MobileBudgetSummaryDto {
  budgetPressure: BudgetPressureSignal;
  budgets: Array<{
    id: string;
    budgetType: string;
    periodStart: string;
    periodEnd: string;
    allocated: number;
    spent: number;
    remaining: number;
  }>;
  /** Always 0 on mobile (D6 web-only). */
  lifetimeContributions: number;
  lifetimeCap: number;
  isApproachingCap: boolean;
}

/**
 * Shape getClientBudgetSummary for the caller's role.
 * Never returns lifetime totals on mobile.
 */
export function shapeMobileBudgetSummary(input: {
  visibility: MobileMoneyVisibility;
  budgets: MobileBudgetSummaryDto['budgets'];
}): MobileBudgetSummaryDto {
  const { visibility, budgets } = input;
  const first = budgets[0];
  const pressure = first
    ? budgetPressureFromAmounts({
        allocated: first.allocated,
        spent: first.spent,
        remaining: first.remaining,
      })
    : 'unknown';

  if (visibility.budgetMode === 'signal' || visibility.budgetMode === 'hide') {
    return {
      budgetPressure: pressure,
      budgets: [],
      lifetimeContributions: 0,
      lifetimeCap: 0,
      isApproachingCap: false,
    };
  }

  // remaining_only — keep remaining; zero allocated/spent so UI cannot show breakdown.
  return {
    budgetPressure: pressure,
    budgets: budgets.map((b) => ({
      id: b.id,
      budgetType: b.budgetType,
      periodStart: b.periodStart,
      periodEnd: b.periodEnd,
      allocated: 0,
      spent: 0,
      remaining: b.remaining,
    })),
    lifetimeContributions: 0,
    lifetimeCap: 0,
    isApproachingCap: false,
  };
}

/**
 * Strip quarter budget on care-plan context for non-elevated mobile readers.
 */
export function stripCarePlanQuarterBudget<T extends { remainingAmount?: number } | null>(
  quarterBudget: T,
  visibility: MobileMoneyVisibility,
): T | null {
  if (!quarterBudget) return null;
  if (visibility.budgetMode === 'signal' || visibility.budgetMode === 'hide') {
    return null;
  }
  if (visibility.budgetMode === 'remaining_only') {
    const row = quarterBudget as Record<string, unknown>;
    const remainingOnly = {
      id: row.id,
      quarter: row.quarter ?? '',
      remainingAmount: Number(row.remainingAmount ?? 0),
      allocatedAmount: 0,
      spentAmount: 0,
      carryOverFromPrior: 0,
      carryOverCapAmount: 0,
    };
    return remainingOnly as unknown as T;
  }
  return quarterBudget;
}
